import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import {
  selectUserIsAppellant,
  selectUserIsAssessor,
  selectUserIsAuthorizedAttorney,
  selectUserIsSolicitor,
} from "./app";
import { RootState, AppDispatch } from ".";
import { RestCallStatus, RestStatus } from "../commontypes/common";
import pako from "pako";
import { selectAddOmitSeason, selectValueClassSeason } from "./admin";
import { Appeal, AppealFees, AppealRecord, AppealCategory, AppealType, BatchAppeal, County, EntryMethod, NewAppeal, PaymentMethod, CrossingAppeals, NewAppealResponse, NewAppealFailure } from "../commontypes/types";

export type SaveSlot = {
  status: RestStatus;
  blq?: string;
  appealNumbers?: string[];
  error?: string;
};


export interface AppealDuplication {
  valuation: boolean,
  classification: boolean,
  addition: boolean,
  abatement: boolean,
  veteranDeduction: boolean,
  disabledVeteranExemption: boolean,
  seniorCitizenOrDisabledPerson: boolean
}

export interface FilingStatus {
  isOpen: boolean,
  closedReason?: string
}

export interface CrossFilingStatus {
  crossedAppealId: string,
  isOpen: boolean,
  closedReason?: string
}

interface NewAppealState {
  appeal: NewAppeal,
  batchAppealList: BatchAppeal[],
  existingAppeals: Appeal[],
  matchingAppealsForBLQ: AppealRecord[],
  appealsThatMayBeCrossed: AppealRecord[],
  flow?: AppealCategory,
  nextCounter?: number,
  feeRestStatus?: RestCallStatus,
  reservationRestStatus?: RestCallStatus,
  existingAppealsRestStatus?: RestCallStatus,
  saveRestStatus?: RestCallStatus,
  preferencesCopiedToContacts: boolean,
  createNewAddition: boolean,
  appealingForTaxpayer?: boolean,
  crossingAppeals: CrossingAppeals,
  appealDuplication: AppealDuplication;
  filingStatus?: FilingStatus,
  filingStatusRestStatus?: RestCallStatus,
  crossFilingStatus: CrossFilingStatus[]
  savedAppealNumbers?: string[],
  failedAppeals?: NewAppealFailure[],
  smartPayPendingRestStatus?: RestCallStatus,
  smartPayPollRestStatus?: RestCallStatus,
  smartPayPendingStatus?: { status: string; appealNumbers?: string[]; failedAppeals?: NewAppealFailure[] },
}

const initialState: NewAppealState = {
  appeal: {
    appealYear: 0,
    county: { key: '', label: '' },
    municipality: { key: '', label: '' },
    filingDescription: ' '
    ,
    property: {
      blq: '',
      address: '',
      cityState: '',
      zip: '',
      lotSize: '',
      classification: { key: '', label: '' },
      key: '',
      owner: '',
      relevantProperty: '',
      purchaseDate: '',
      purchasePrice: 0,
      tenData: null,
      currentAssessment: {
        total: 0,
        land: 0,
        buildingImprovement: 0,
        abatement: 0,
      },
    },
    valuation: {
      appealing: false,
      taxCourtPending: false,
    },
    classification: {
      appealing: false,
    },
    deduction: {
      veteranDeduction: false,
      disabledVeteranExemption: false,
      seniorCitizenOrDisabledPerson: false,
      abatement: false,
    },
    hearingDate: null,
    hearingLocation: null,
    entryMethod: EntryMethod.ONLINE,
    entryPaymentMethod: PaymentMethod.ONLINE_CARD,
    contact: {
      mainEmailCheckbox: false,
      mainPhoneCheckbox: false,
    },
    additionalContact: {
      notify: false,
    },
    thirdPartyContact: {
      notify: false,
    },
    authorizedAttorneyContact: {
      notify: false,
    },
    creditCardPayment: {
      paidAppeal: false
    },
    receiptId: '',
    fees: {
      propertyValuationFee: 0,
      deductionExemptionFee: 0,
      propertyClassificationFee: 0,
      convenienceFee: 0,
      total: 0,
    },
    crossAppeal: {
      isCrossAppeal: false,
    },
    notes: null,
    geolocation: {},
    deleted: false,
    tenRecords: null,
    improvementRecord: {},
    tenAppeal: false,
    appVersion: process.env.REACT_APP_VERSION || "0.0.0",
    creatorObj: {},
  },
  batchAppealList: [],
  existingAppeals: [],
  matchingAppealsForBLQ: [],
  appealsThatMayBeCrossed: [],
  preferencesCopiedToContacts: false,
  createNewAddition: false,
  appealingForTaxpayer: true,
  crossingAppeals: {
    valuation: { isCrossAppeal: false },
    classification: { isCrossAppeal: false },
    deduction: {
      abatement: { isCrossAppeal: false },
      disabledVeteranExemption: { isCrossAppeal: false },
      seniorCitizenOrDisabledPerson: { isCrossAppeal: false },
      veteranDeduction: { isCrossAppeal: false }
    },
    addOmit: { isCrossAppeal: false }
  },
  appealDuplication: {
    valuation: false,
    classification: false,
    addition: false,
    abatement: false,
    veteranDeduction: false,
    disabledVeteranExemption: false,
    seniorCitizenOrDisabledPerson: false
  },
  crossFilingStatus: []
}

const initializeContacts = (state: NewAppealState, appealingForTaxpayer: boolean) => {
  if (appealingForTaxpayer) {
    state.appeal.thirdPartyContact = {};
    state.appeal.authorizedAttorneyContact = {};
  } else {
    state.appeal.thirdPartyContact.isThirdPartyContact = true;
  }
};

export const calculateFee = createAsyncThunk<
  AppealFees,
  void,
  { state: RootState; getState: () => RootState, dispatch: AppDispatch; requestId: string }
>(
  "appeal/calculateFee",
  async (_void, { getState }) => {
    const state = getState();
    const county = selectCounty(state);
    const userIsAssessor = selectUserIsAssessor(state);
    const userIsSolicitor = selectUserIsSolicitor(state);
    const userIsAuthorizedAttorney = selectUserIsAuthorizedAttorney(state);
    const userIsAppellant = selectUserIsAppellant(state);
    const filingDescription = selectFilingDescription(state);
    if (userIsAssessor || userIsSolicitor || userIsAuthorizedAttorney || (filingDescription === "A")) {
      return { propertyValuationFee: 0, deductionExemptionFee: 0, propertyClassificationFee: 0, convenienceFee: 0, total: 0 };
    }
    const response = await axios
      .get(`${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${county.key}/fee`, {
        params: {
          currentAssessmentTotal: selectCurrentAssessmentTotal(state),
          currentAdditionAmount: selectCurrentAdditionAmount(state),
          appealingClassification: selectAppealingClassification(state),
          tenAppeal: selectIsTenAppeal(state),
          appealingValuation: selectAppealingValuation(state),
          appealingAbatementDenial: selectDeductionAbatement(state),
        },
      });
    const fees = response.data.fees;
    if (!userIsAppellant) {
      fees.total = fees.total - fees.convenienceFee;
      fees.convenienceFee = 0;
    }
    return fees;
  }
);

export const reserveAppealCounters = createAsyncThunk<
  { nextCounter: number },
  { count: number; appealYear: number },
  { state: RootState; getState: () => RootState; dispatch: AppDispatch; requestId: string }
>(
  "appeal/reserveAppealCounters",
  async ({ count, appealYear }, { getState }) => {
    const state = getState();
    const county = selectCounty(state);
    const municipality = selectMunicipality(state);
    const isTenAppeal = selectIsTenAppeal(state);
    const updateResponse = await axios
      .post(`${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${county.key}/municipalities/${municipality.key}/appealcount`, {
        count: count,
        isTen: isTenAppeal,
        year: appealYear
      },
        {
        });
    return { nextCounter: updateResponse.data.firstCount };
  }
);

export type GetExistingAppealsForMunicipalitiesParams = {
  county: County;
  appealYear: number;
  isTen: boolean;
  municipalityIds: string[];
  filterForAuthAttorney?: boolean;
};
export const getExistingAppealsForMunicipalities = createAsyncThunk<
  Appeal[],
  GetExistingAppealsForMunicipalitiesParams,
  { state: RootState; getState: () => RootState; dispatch: AppDispatch; requestId: string }
>(
  "appeal/getExistingAppeals",
  async ({ county, appealYear, isTen, municipalityIds, filterForAuthAttorney }, { getState }) => {
    const state = getState();
    const seasonConfiguration = isTen ? selectAddOmitSeason(state) : selectValueClassSeason(state);
    const judgmentSince = seasonConfiguration?.judgmentSinceFilter;
    const type = isTen ? 'ten' : 'twenty';
    const path = `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${county.key}/appeals?appealYear=${appealYear}&townId=${municipalityIds}&type=${type}&judgmentSince=${judgmentSince}`;
    const response = await axios.get(path);
    let data = JSON.parse(Buffer.from(pako.inflate(response.data.data)).toString()) as Appeal[];
    if (filterForAuthAttorney) {
      data = data.filter(obj => obj.APPEAL.authorizedAttorneyContact?.firstName);
    }
    return data;
  }
);

export type CheckFilingStatusParams = {
  countyKey: string;
  appealType: AppealType;
  municipalityKey: string;
  crossedAppealId?: string;
};

export const checkFilingStatus = createAsyncThunk<
  FilingStatus,
  CheckFilingStatusParams,
  { state: RootState; dispatch: AppDispatch }
>(
  "appeal/checkFilingStatus",
  async ({ countyKey, appealType, municipalityKey, crossedAppealId }) => {
    let path = `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${countyKey}/filingstatus?appealType=${encodeURIComponent(appealType)}&municipalityKey=${municipalityKey}`;
    if (crossedAppealId) {
      path += `&crossedAppealId=${crossedAppealId}`;
    }
    const response = await axios.get(path);
    return response.data;
  }
);

export type CreateSmartPayPendingParams = {
  referenceId: string;
  appeal: NewAppeal;
  appealTypesToSave: AppealType[];
  customerId: string;
  locationId: string;
  smartPayHost: string;
};

export const createSmartPayPending = createAsyncThunk<
  { referenceId: string; sessionKey?: string },
  CreateSmartPayPendingParams,
  { state: RootState; getState: () => RootState; dispatch: AppDispatch }
>(
  "appeal/createSmartPayPending",
  async ({ referenceId, appeal, appealTypesToSave, customerId, locationId, smartPayHost }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const crossingAppeals = selectCrossingAppeals(state);
      const batchValuationAppeals = selectBatchAppealList(state);
      const isAppealingForTaxpayer = selectAppealingForTaxpayer(state);
      const isOwnerRepresentedByLawyer = selectAdditionalContactIsLawyer(state);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${appeal.county.key}/smartpay/pending`,
        { appeal, appealTypesToSave, batchValuationAppeals, crossingAppeals, isAppealingForTaxpayer, isOwnerRepresentedByLawyer, referenceId, customerId, locationId, smartPayHost }
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data);
    }
  }
);

export type GetSmartPayPendingParams = {
  countyKey: string;
  referenceId: string;
};

export type SmartPayPendingStatus = {
  referenceId: string;
  status: string;
  appealNumbers?: string;
  failedAppeals?: string;
  countyId: string;
  createdAt: string;
};

export const getSmartPayPending = createAsyncThunk<
  SmartPayPendingStatus,
  GetSmartPayPendingParams,
  { state: RootState; dispatch: AppDispatch }
>(
  "appeal/getSmartPayPending",
  async ({ countyKey, referenceId }) => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${countyKey}/smartpay/pending/${referenceId}`
    );
    return response.data;
  }
);

export type SaveAppealParams = {
  appeal: NewAppeal;
  appealTypesToSave: AppealType[];
};

export const saveAppeal = createAsyncThunk<
  NewAppealResponse,
  SaveAppealParams,
  { state: RootState; getState: () => RootState; dispatch: AppDispatch; requestId: string }
>(
  "appeal/saveAppeal",
  async ({ appeal, appealTypesToSave }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const crossingAppeals = selectCrossingAppeals(state);
      const batchValuationAppeals = selectBatchAppealList(state);
      const isAppealingForTaxpayer = selectAppealingForTaxpayer(state);
      const isOwnerRepresentedByLawyer = selectAdditionalContactIsLawyer(state);

      const response = await axios
        .post(`${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${appeal.county.key}/appeals`, {
          appeal,
          appealTypesToSave,
          crossingAppeals,
          batchValuationAppeals,
          isAppealingForTaxpayer,
          isOwnerRepresentedByLawyer
        },
          {
          });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }

  }
);

export const appealSlice = createSlice({
  name: 'newAppeal',
  initialState: initialState,
  reducers: {
    setFlow: (state, action) => {
      state.flow = action.payload;
    },

    clearFlow: (state) => {
      state.flow = undefined;
    },

    setCounty: (state, action) => {
      const { key, label } = action.payload;
      // const taxBoard = key ? countyTaxBoards[key] : {};
      // state.appeal.county = {key, label, ...taxBoard};
      state.appeal.county = { key, label };
    },

    setMunicipality: (state, action) => {
      state.appeal.municipality = action.payload;
    },

    unsetMunicipality: state => {
      state.appeal.municipality = initialState.appeal.municipality;
    },

    setProperty: (state, action) => {
      state.appeal.property = action.payload;
    },

    unsetProperty: state => {
      state.appeal.property = initialState.appeal.property;
    },

    unsetValuation: state => {
      state.appeal.valuation = initialState.appeal.valuation;
    },

    setValuation: (state, action) => {
      state.appeal.valuation = action.payload;
    },

    unsetFees: state => {
      state.appeal.fees = initialState.appeal.fees;
      state.feeRestStatus = { status: RestStatus.NOT_STARTED };
    },

    setBatchAppealList: (state, action) => {
      state.batchAppealList = action.payload;
    },

    unsetBatchAppealList: state => {
      state.batchAppealList = [];
    },

    unsetCrossingAppeal: (state) => {
      state.crossingAppeals = initialState.crossingAppeals;
    },

    setIsValuationCrossAppeal: (state, action) => {
      state.crossingAppeals.valuation.isCrossAppeal = action.payload;
    },
    setValuationOtherAppeal: (state, action) => {
      state.crossingAppeals.valuation.otherAppeal = action.payload;
    },
    unsetCrossingValuationAppeal: (state, action) => {
      state.crossingAppeals.valuation = { isCrossAppeal: action.payload };
    },

    setIsClassificationCrossAppeal: (state, action) => {
      state.crossingAppeals.classification.isCrossAppeal = action.payload;
    },
    setClassificationOtherAppeal: (state, action) => {
      state.crossingAppeals.classification.otherAppeal = action.payload;
    },
    unsetCrossingClassificationAppeal: (state, action) => {
      state.crossingAppeals.classification = { isCrossAppeal: action.payload };
    },

    setIsAddOmitCrossAppeal: (state, action) => {
      state.crossingAppeals.addOmit.isCrossAppeal = action.payload;
    },
    setAddOmitOtherAppeal: (state, action) => {
      state.crossingAppeals.addOmit.otherAppeal = action.payload;
    },
    unsetCrossingAddOmitAppeal: (state, action) => {
      state.crossingAppeals.addOmit = { isCrossAppeal: action.payload };
    },

    setIsDeductionAbatementCrossAppeal: (state, action) => {
      state.crossingAppeals.deduction.abatement.isCrossAppeal = action.payload;
    },
    setDeductionAbatementOtherAppeal: (state, action) => {
      state.crossingAppeals.deduction.abatement.otherAppeal = action.payload;
    },
    unsetCrossingDeductionAbatementAppeal: (state, action) => {
      state.crossingAppeals.deduction.abatement = { isCrossAppeal: action.payload };
    },

    setIsDeductionVeteranCrossAppeal: (state, action) => {
      state.crossingAppeals.deduction.veteranDeduction.isCrossAppeal = action.payload;
    },
    setDeductionVeteranOtherAppeal: (state, action) => {
      state.crossingAppeals.deduction.veteranDeduction.otherAppeal = action.payload;
    },
    unsetCrossingDeductionVeteranAppeal: (state, action) => {
      state.crossingAppeals.deduction.veteranDeduction = { isCrossAppeal: action.payload };
    },

    setIsDeductionDisabledVeteranCrossAppeal: (state, action) => {
      state.crossingAppeals.deduction.disabledVeteranExemption.isCrossAppeal = action.payload;
    },
    setDeductionDisabledVeteranOtherAppeal: (state, action) => {
      state.crossingAppeals.deduction.disabledVeteranExemption.otherAppeal = action.payload;
    },
    unsetCrossingDeductionDisabledVeteranAppeal: (state, action) => {
      state.crossingAppeals.deduction.disabledVeteranExemption = { isCrossAppeal: action.payload };
    },

    setIsDeductionSeniorCrossAppeal: (state, action) => {
      state.crossingAppeals.deduction.seniorCitizenOrDisabledPerson.isCrossAppeal = action.payload;
    },
    setDeductionSeniorOtherAppeal: (state, action) => {
      state.crossingAppeals.deduction.seniorCitizenOrDisabledPerson.otherAppeal = action.payload;
    },
    unsetCrossingDeductionSeniorAppeal: (state, action) => {
      state.crossingAppeals.deduction.seniorCitizenOrDisabledPerson = { isCrossAppeal: action.payload };
    },


    setIsTenAppeal: (state, action) => {
      state.appeal.tenAppeal = action.payload;
    },

    setAppealYear: (state, action) => {
      state.appeal.appealYear = action.payload;
    },

    setHearingType: (state, action) => {
      state.appeal.hearingType = action.payload;
    },

    setAppealingValuation: (state, action) => {
      state.appeal.valuation.appealing = action.payload;
    },

    setDuplicateClassificationAppeal: (state, action) => {
      state.appealDuplication.classification = action.payload;
    },

    setDuplicateValuationAppeal: (state, action) => {
      state.appealDuplication.valuation = action.payload;
    },

    setDuplicateAdditionAppeal: (state, action) => {
      state.appealDuplication.addition = action.payload;
    },

    setDuplicateAbatementDeductionAppeal: (state, action) => {
      state.appealDuplication.abatement = action.payload;
    },

    setDuplicateVeteranDeductionAppeal: (state, action) => {
      state.appealDuplication.veteranDeduction = action.payload;
    },

    setDuplicateDisabledVeteranExemptionAppeal: (state, action) => {
      state.appealDuplication.disabledVeteranExemption = action.payload;
    },

    setDuplicateSeniorCitizenOrDisabledPersonAppeal: (state, action) => {
      state.appealDuplication.seniorCitizenOrDisabledPerson = action.payload;
    },

    setPurchasePrice: (state, action) => {
      state.appeal.valuation.purchasePrice = action.payload;
      state.appeal.property.purchasePrice = action.payload;
    },

    setPurchaseDate: (state, action) => {
      state.appeal.valuation.purchaseDate = action.payload;
      state.appeal.property.purchaseDate = action.payload;
    },

    setRequestedTotalAssessment: (state, action) => {
      state.appeal.valuation.requestedTotal = action.payload;
    },

    unsetRequestedTotalAssessment: state => {
      state.appeal.valuation.requestedTotal = undefined;
    },

    setTaxCourtPending: (state, action) => {
      state.appeal.valuation.taxCourtPending = action.payload;
    },

    unsetTaxCourtPending: state => {
      state.appeal.valuation.taxCourtPending = undefined;
    },

    setReasonForAppeal: (state, action) => {
      state.appeal.valuation.reason = action.payload;
    },

    unsetReasonForAppeal: state => {
      state.appeal.valuation.reason = undefined;
    },

    setAppealingClass: (state, action) => {
      state.appeal.classification.appealing = action.payload;
    },

    setRequestedClass: (state, action) => {
      state.appeal.classification.requestedClass = action.payload;
    },

    setCurrentClass: (state, action) => {
      state.appeal.classification.currentClass = action.payload;
    },

    unsetRequestedClass: state => {
      state.appeal.classification.requestedClass = undefined;
    },

    setVeteranDeduction: (state, action) => {
      state.appeal.deduction.veteranDeduction = action.payload;
    },

    setDisabledVeteranExemption: (state, action) => {
      state.appeal.deduction.disabledVeteranExemption = action.payload;
    },

    setSeniorCitizenOrDisabledPersonDeduction: (state, action) => {
      state.appeal.deduction.seniorCitizenOrDisabledPerson = action.payload;
    },

    setAbatementDeduction: (state, action) => {
      state.appeal.deduction.abatement = action.payload;
    },

    //Begin Contact
    setContactFirstName: (state, action) => {
      state.appeal.contact.firstName = action.payload;
    },
    setContactAddress: (state, action) => {
      state.appeal.contact.address = action.payload;
    },
    setContactCity: (state, action) => {
      state.appeal.contact.city = action.payload;
    },

    setContactZipCode: (state, action) => {
      state.appeal.contact.zipCode = action.payload;
    },
    setContactEmailAddress: (state, action) => {
      state.appeal.contact.emailAddress = action.payload;
    },
    setContactDayTimePhone: (state, action) => {
      state.appeal.contact.dayTimePhone = action.payload;
    },
    setMainEmailCheckbox: (state, action) => {
      state.appeal.contact.mainEmailCheckbox = action.payload;
    },
    setMainPhoneCheckbox: (state, action) => {
      state.appeal.contact.mainPhoneCheckbox = action.payload;
    },

    //Begin Additional Contact
    setNotifyAdditionalContact: (state, action) => {
      state.appeal.additionalContact.notify = action.payload;
    },
    setAdditionalContactFirstName: (state, action) => {
      state.appeal.additionalContact.firstName = action.payload;
    },
    setAdditionalContactAddress: (state, action) => {
      state.appeal.additionalContact.address = action.payload;
    },
    setAdditionalContactCity: (state, action) => {
      state.appeal.additionalContact.city = action.payload;
    },
    setAdditionalContactZipCode: (state, action) => {
      state.appeal.additionalContact.zipCode = action.payload;
    },
    setAdditionalContactEmailAddress: (state, action) => {
      state.appeal.additionalContact.emailAddress = action.payload;
    },
    setAdditionalContactDayTimePhone: (state, action) => {
      state.appeal.additionalContact.dayTimePhone = action.payload;
    },
    setACLawyerLicense: (state, action) => {
      state.appeal.additionalContact.aCLawyerLicense = action.payload;
    },
    setContactEmailCheckbox: (state, action) => {
      state.appeal.additionalContact.contactEmailCheckbox = action.payload;
    },
    setContactPhoneCheckbox: (state, action) => {
      state.appeal.additionalContact.contactPhoneCheckbox = action.payload;
    },
    setIsLawyer: (state, action) => {
      state.appeal.additionalContact.isLawyer = action.payload;
    },
    setIsAdditionalContact: (state, action) => {
      state.appeal.additionalContact.isAdditionalContact = action.payload;
    },
    unsetIsAdditionalContact: (state) => {
      state.appeal.additionalContact = {};
    },

    //Begin ThirdParty Contact
    setNotifyThirdPartyContact: (state, action) => {
      state.appeal.thirdPartyContact.notify = action.payload;
    },
    setThirdPartyContactFirstName: (state, action) => {
      state.appeal.thirdPartyContact.firstName = action.payload;
    },

    setThirdPartyContactAddress: (state, action) => {
      state.appeal.thirdPartyContact.address = action.payload;
    },
    setThirdPartyContactCity: (state, action) => {
      state.appeal.thirdPartyContact.city = action.payload;
    },

    setThirdPartyContactZipCode: (state, action) => {
      state.appeal.thirdPartyContact.zipCode = action.payload;
    },
    setThirdPartyContactEmailAddress: (state, action) => {
      state.appeal.thirdPartyContact.emailAddress = action.payload;
    },
    setThirdPartyContactDayTimePhone: (state, action) => {
      state.appeal.thirdPartyContact.dayTimePhone = action.payload;
    },
    setIsThirdPartyContact: (state, action) => {
      state.appeal.thirdPartyContact.isThirdPartyContact = action.payload;
    },
    unsetIsThirdPartyContact: (state) => {
      state.appeal.thirdPartyContact = {};
    },
    setThirdPartyEmailCheckbox: (state, action) => {
      state.appeal.thirdPartyContact.thirdPartyEmailCheckbox = action.payload;
    },
    setThirdPartyPhoneCheckbox: (state, action) => {
      state.appeal.thirdPartyContact.thirdPartyPhoneCheckbox = action.payload;
    },
    setAuthorizedAttorneyContactFirstName: (state, action) => {
      state.appeal.authorizedAttorneyContact.firstName = action.payload;
    },
    setAuthorizedAttorneyContactAddress: (state, action) => {
      state.appeal.authorizedAttorneyContact.address = action.payload;
    },
    setAuthorizedAttorneyContactCity: (state, action) => {
      state.appeal.authorizedAttorneyContact.city = action.payload;
    },
    setAuthorizedAttorneyContactZipCode: (state, action) => {
      state.appeal.authorizedAttorneyContact.zipCode = action.payload;
    },
    setAuthorizedAttorneyContactEmailAddress: (state, action) => {
      state.appeal.authorizedAttorneyContact.emailAddress = action.payload;
    },
    setAuthorizedAttorneyContactDayTimePhone: (state, action) => {
      state.appeal.authorizedAttorneyContact.dayTimePhone = action.payload;
    },
    setIsAuthorizedAttorneyContact: (state, action) => {
      state.appeal.authorizedAttorneyContact.isAuthorizedAttorneyContact = action.payload;
    },
    unsetIsAuthorizedAttorneyContact: (state) => {
      state.appeal.authorizedAttorneyContact = {};
    },
    setAuthorizedAttorneyEmailCheckbox: (state, action) => {
      state.appeal.authorizedAttorneyContact.authorizedAttorneyEmailCheckbox = action.payload;
    },
    setAuthorizedAttorneyPhoneCheckbox: (state, action) => {
      state.appeal.authorizedAttorneyContact.authorizedAttorneyPhoneCheckbox = action.payload;
    },
    setAALawyerLicense: (state, action) => {
      state.appeal.authorizedAttorneyContact.aALawyerLicense = action.payload;
    },

    // End

    setTenRecords: (state, action) => {
      state.appeal.tenRecords = action.payload;
    },

    setSelectedTen: (state, action) => {
      state.appeal.improvementRecord.originalRecord = action.payload;
      state.appeal.improvementRecord.listType = action.payload?.listType;
      state.appeal.improvementRecord.taxYear = action.payload?.taxYear;
    },
    setAdditionListType: (state, action) => {
      state.appeal.improvementRecord.listType = action.payload;
    },
    setAdditionTaxYear: (state, action) => {
      state.appeal.improvementRecord.taxYear = action.payload;
    },
    setAdditionPropertyClass: (state, action) => {
      state.appeal.improvementRecord.propertyClass = action.payload;
    },
    setRequestedAdditionDate: (state, action) => {
      state.appeal.improvementRecord.requestedAdditionDate = action.payload;
    },

    setRequestedAdditionMonth: (state, action) => {
      state.appeal.improvementRecord.requestedAdditionMonth = action.payload;
    },

    setRequestedAdditionAmount: (state, action) => {
      state.appeal.improvementRecord.requestedAdditionAmount = action.payload;
    },


    setRequestedAdditionLandAmount: (state, action) => {
      state.appeal.improvementRecord.requestedAdditionLand = action.payload;
    },


    setRequestedAdditionImprovementAmount: (state, action) => {
      state.appeal.improvementRecord.requestedAdditionImprovement = action.payload;
    },


    setRequestedAdditionAbatementAmount: (state, action) => {
      state.appeal.improvementRecord.requestedAdditionAbatement = action.payload;
    },

    setEntryMethod: (state, action) => {
      state.appeal.entryMethod = action.payload;
    },
    setEntryPaymentMethod: (state, action) => {
      state.appeal.entryPaymentMethod = action.payload;
    },

    setFees: (state, action) => {
      state.appeal.fees = action.payload;
    },

    setReceiptId: (state, action) => {
      state.appeal.receiptId = action.payload;
    },

    setScanFile: (state, action) => {
      state.appeal.scanFile = action.payload;
    },

    setScanDenial: (state, action) => {
      state.appeal.scanDenial = action.payload;
    },

    setFilingDescription: (state, action) => {
      state.appeal.filingDescription = action.payload;
    },
    setIsBatchTemplate: (state, action) => {
      state.appeal.isBatchTemplate = action.payload;
    },

    // saveComparableSales: (state, data) => {
    //   state.appeal.comparableSales = action.payload;
    // },

    // setComparableSaleBlq: (list, data) => {
    //   return [
    //     ...list.slice(0, data.index),
    //     {
    //       ...listdata.index,
    //       blq: data.blq,
    //     },
    //     ...list.slice(data.index + 1),
    //   ]
    // },

    // setComparableSaleAddress: (list, data) => {
    //   return [
    //     ...list.slice(0, data.index),
    //     {
    //       ...listdata.index,
    //       address: data.address,
    //     },
    //     ...list.slice(data.index + 1),
    //   ]
    // },

    // setComparableSalePrice: (list, data) => {
    //   return [
    //     ...list.slice(0, data.index),
    //     {
    //       ...listdata.index,
    //       salePrice: data.salePrice,
    //     },
    //     ...list.slice(data.index + 1),
    //   ]
    // },

    // setComparableSaleDate: (list, data) => {
    //   return [
    //     ...list.slice(0, data.index),
    //     {
    //       ...listdata.index,
    //       saleDate: data.saleDate,
    //     },
    //     ...list.slice(data.index + 1),
    //   ]
    // },

    // removeComparableSale: (list, index) => {
    //   return ...list.slice(0, index), ...list.slice(index + 1)
    // },

    // addComparableSale: (state, key) => ({
    //   ...state,
    //   comparableSales: [
    //     ...state.comparableSales,
    //     {
    //       key: key,
    //       blq: '',
    //       address: '',
    //       salePrice: '',
    //       saleDate: '',
    //     },
    //   ],
    // }),

    // updateComparableSale: (state, data) => {
    //   const { key, row } = data
    //   return {
    //     ...state,
    //     comparableSales: state.appeal.comparableSales.map(comp => {
    //       if (comp.key !== key) {
    //         return comp
    //       }

    //       return {
    //         key: row.key,
    //         blq: row.blq,
    //         address: row.address,
    //         salePrice: row.salePrice,
    //         saleDate: row.saleDate,
    //       }
    //     }),
    //   }
    // },

    rememberExistingAppealsForBLQ: (state, action) => {
      state.matchingAppealsForBLQ = action.payload;
    },
    rememberAppealsThatMayBeCrossed: (state, action) => {
      state.appealsThatMayBeCrossed = action.payload;
    },
    clearAppeal: (state) => {
      state.appeal = initialState.appeal;
      state.matchingAppealsForBLQ = [];
      state.appealsThatMayBeCrossed = [];
      state.saveRestStatus = undefined;
      state.preferencesCopiedToContacts = false;
      state.createNewAddition = false;
      state.crossFilingStatus = [];
      state.filingStatus = undefined;
      state.smartPayPendingRestStatus = undefined;
      state.savedAppealNumbers = undefined;
      state.failedAppeals = undefined;
      state.smartPayPendingStatus = undefined;
    },
    setPreferencesCopiedToContacts: (state, action) => {
      state.preferencesCopiedToContacts = action.payload;
    },
    setCreateNewAddition: (state, action) => {
      state.createNewAddition = action.payload;
    },
    setAppealingForTaxpayer: (state, action) => {
      state.appealingForTaxpayer = action.payload;
      initializeContacts(state, action.payload);
    },

    restorePendingAppealState: (state, action: PayloadAction<{
      appeal: NewAppeal;
      crossingAppeals?: CrossingAppeals;
      batchValuationAppeals?: BatchAppeal[];
      isAppealingForTaxpayer?: boolean;
      existingAppeals: Appeal[];
      matchingAppealsForBLQ: AppealRecord[];
      appealsThatMayBeCrossed: AppealRecord[];
      createNewAddition: boolean;
      appealDuplication: AppealDuplication;
      filingStatus?: FilingStatus;
      crossFilingStatus: CrossFilingStatus[];
    }>) => {
      const { appeal, crossingAppeals, batchValuationAppeals, isAppealingForTaxpayer, existingAppeals, matchingAppealsForBLQ, appealsThatMayBeCrossed, appealDuplication, createNewAddition, filingStatus, crossFilingStatus } = action.payload;
      state.appeal = appeal;
      if (crossingAppeals) state.crossingAppeals = crossingAppeals;
      if (batchValuationAppeals) state.batchAppealList = batchValuationAppeals;
      if (isAppealingForTaxpayer !== undefined) state.appealingForTaxpayer = isAppealingForTaxpayer;
      state.existingAppeals = existingAppeals;
      state.matchingAppealsForBLQ = matchingAppealsForBLQ;
      state.appealsThatMayBeCrossed = appealsThatMayBeCrossed;
      state.createNewAddition = createNewAddition;
      state.appealDuplication = appealDuplication;
      state.saveRestStatus = undefined;
      state.smartPayPendingRestStatus = undefined;
      state.smartPayPollRestStatus = undefined;
      state.smartPayPendingStatus = undefined;
      state.filingStatus = filingStatus;
      state.crossFilingStatus = crossFilingStatus;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(reserveAppealCounters.pending, (state) => {
        state.reservationRestStatus = { status: RestStatus.SAVING };
      })
      .addCase(reserveAppealCounters.fulfilled, (state, action) => {
        const { nextCounter } = action.payload;
        state.reservationRestStatus = { status: RestStatus.SAVED };
        state.nextCounter = nextCounter;
      })
      .addCase(reserveAppealCounters.rejected, (state, action) => {
        state.reservationRestStatus = { status: RestStatus.SAVE_FAILED, error: action.error.message };
      })
      .addCase(saveAppeal.pending, (state) => {
        state.saveRestStatus = { status: RestStatus.SAVING };
        state.savedAppealNumbers = undefined;
        state.failedAppeals = undefined;
      })
      .addCase(saveAppeal.fulfilled, (state, action) => {
        const { appealNumbers, failedAppeals } = action.payload;
        console.log(`AppealNumbers: ${appealNumbers.join(', ')}`);
        state.saveRestStatus = { status: RestStatus.SAVED };
        state.savedAppealNumbers = appealNumbers;
        state.failedAppeals = failedAppeals;
      })
      .addCase(saveAppeal.rejected, (state, action) => {
        state.saveRestStatus = { status: RestStatus.SAVE_FAILED, error: action.payload as string };
      })
      .addCase(calculateFee.pending, (state) => {
        state.feeRestStatus = { status: RestStatus.LOADING };
      })
      .addCase(calculateFee.fulfilled, (state, action) => {
        state.feeRestStatus = { status: RestStatus.LOADED };
        state.appeal.fees = action.payload;
        if (action.payload.total === 0) {
          state.appeal.entryPaymentMethod = PaymentMethod.NONE;
        }
      })
      .addCase(calculateFee.rejected, (state, action) => {
        state.feeRestStatus = { status: RestStatus.LOAD_FAILED, error: action.error.message };
      })
      .addCase(getExistingAppealsForMunicipalities.pending, (state) => {
        state.existingAppealsRestStatus = { status: RestStatus.LOADING };
        state.existingAppeals = [];
      })
      .addCase(getExistingAppealsForMunicipalities.fulfilled, (state, action) => {
        state.existingAppealsRestStatus = { status: RestStatus.LOADED };
        state.existingAppeals = action.payload;
      })
      .addCase(getExistingAppealsForMunicipalities.rejected, (state, action) => {
        state.existingAppealsRestStatus = { status: RestStatus.LOAD_FAILED, error: action.error.message };
      })
      .addCase(checkFilingStatus.pending, (state) => {
        state.filingStatusRestStatus = { status: RestStatus.LOADING };
      })
      .addCase(checkFilingStatus.fulfilled, (state, action) => {
        const { crossedAppealId } = action.meta.arg;
        if (crossedAppealId) {
          state.crossFilingStatus = [...state.crossFilingStatus, { crossedAppealId, ...action.payload }];
        } else {
          state.filingStatus = action.payload;
        }
        state.filingStatusRestStatus = { status: RestStatus.LOADED };
      })
      .addCase(checkFilingStatus.rejected, (state, action) => {
        state.filingStatusRestStatus = { status: RestStatus.LOAD_FAILED, error: action.error.message };
      })
      .addCase(createSmartPayPending.pending, (state) => {
        state.smartPayPendingRestStatus = { status: RestStatus.SAVING };
      })
      .addCase(createSmartPayPending.fulfilled, (state) => {
        state.smartPayPendingRestStatus = { status: RestStatus.SAVED };
      })
      .addCase(createSmartPayPending.rejected, (state, action) => {
        state.smartPayPendingRestStatus = { status: RestStatus.SAVE_FAILED, error: action.error.message };
      })
      .addCase(getSmartPayPending.pending, (state) => {
        state.smartPayPollRestStatus = { status: RestStatus.LOADING };
      })
      .addCase(getSmartPayPending.fulfilled, (state, action) => {
        state.smartPayPollRestStatus = { status: RestStatus.LOADED };
        const { status, appealNumbers, failedAppeals } = action.payload;
        const parsedAppealNumbers = appealNumbers ? appealNumbers.split(",") : undefined;
        const parsedFailedAppeals = failedAppeals ? JSON.parse(failedAppeals) as NewAppealFailure[] : undefined;
        state.smartPayPendingStatus = { status, appealNumbers: parsedAppealNumbers, failedAppeals: parsedFailedAppeals };
        if (status === "APPROVED") {
          state.savedAppealNumbers = parsedAppealNumbers;
          state.failedAppeals = parsedFailedAppeals;
          state.saveRestStatus = { status: RestStatus.SAVED };
        }
      })
      .addCase(getSmartPayPending.rejected, (state, action) => {
        state.smartPayPollRestStatus = { status: RestStatus.LOAD_FAILED, error: action.error.message };
      })
  }
});
export const { setCounty, setMunicipality, unsetMunicipality, setProperty, unsetProperty, setBatchAppealList, unsetBatchAppealList, unsetCrossingAppeal, setIsValuationCrossAppeal, setValuationOtherAppeal, unsetCrossingValuationAppeal,
  setIsClassificationCrossAppeal, setClassificationOtherAppeal, unsetCrossingClassificationAppeal, setIsAddOmitCrossAppeal, setAddOmitOtherAppeal, unsetCrossingAddOmitAppeal,
  setIsDeductionAbatementCrossAppeal, setDeductionAbatementOtherAppeal, unsetCrossingDeductionAbatementAppeal,
  setIsDeductionVeteranCrossAppeal, setDeductionVeteranOtherAppeal, unsetCrossingDeductionVeteranAppeal,
  setIsDeductionDisabledVeteranCrossAppeal, setDeductionDisabledVeteranOtherAppeal, unsetCrossingDeductionDisabledVeteranAppeal,
  setIsDeductionSeniorCrossAppeal, setDeductionSeniorOtherAppeal, unsetCrossingDeductionSeniorAppeal,
  setDuplicateValuationAppeal,
  setAppealingValuation, setPurchasePrice,
  setPurchaseDate, setRequestedTotalAssessment, unsetRequestedTotalAssessment, setTaxCourtPending, unsetTaxCourtPending, setReasonForAppeal,
  unsetReasonForAppeal, setAppealingClass, setCurrentClass, setRequestedClass, unsetRequestedClass, setVeteranDeduction, setDisabledVeteranExemption,
  setSeniorCitizenOrDisabledPersonDeduction, setAbatementDeduction, setContactFirstName, setContactAddress, setContactCity,
  setContactZipCode, setContactEmailAddress, setContactDayTimePhone, setMainEmailCheckbox, setMainPhoneCheckbox,
  setNotifyAdditionalContact, setAdditionalContactFirstName, setAdditionalContactAddress, setAdditionalContactCity,
  setAdditionalContactZipCode, setAdditionalContactEmailAddress, setAdditionalContactDayTimePhone,
  setACLawyerLicense, setContactEmailCheckbox, setContactPhoneCheckbox, setIsLawyer, setIsAdditionalContact, unsetIsAdditionalContact,
  setNotifyThirdPartyContact, setThirdPartyContactFirstName, setThirdPartyContactAddress, setThirdPartyContactCity,
  setThirdPartyContactZipCode, setThirdPartyEmailCheckbox, setThirdPartyPhoneCheckbox, setThirdPartyContactEmailAddress,
  setThirdPartyContactDayTimePhone, setIsThirdPartyContact, unsetIsThirdPartyContact, setAALawyerLicense,
  setAuthorizedAttorneyContactFirstName, setAuthorizedAttorneyContactAddress,
  setAuthorizedAttorneyContactCity, setAuthorizedAttorneyContactZipCode, setAuthorizedAttorneyContactEmailAddress,
  setAuthorizedAttorneyContactDayTimePhone, setAuthorizedAttorneyEmailCheckbox, setAuthorizedAttorneyPhoneCheckbox,
  setIsAuthorizedAttorneyContact, unsetIsAuthorizedAttorneyContact,
  setEntryMethod, setEntryPaymentMethod, setReceiptId, setScanFile, setScanDenial, setFilingDescription, setFees,
  setIsTenAppeal, setTenRecords, setSelectedTen, setRequestedAdditionDate, setRequestedAdditionAmount, setRequestedAdditionLandAmount,
  setRequestedAdditionImprovementAmount, setRequestedAdditionAbatementAmount, setRequestedAdditionMonth, setHearingType,
  clearAppeal, setAppealYear, setIsBatchTemplate, setFlow, clearFlow, setValuation, unsetValuation, unsetFees,
  setAdditionListType, setAdditionTaxYear, rememberExistingAppealsForBLQ, rememberAppealsThatMayBeCrossed, setAdditionPropertyClass,
  setPreferencesCopiedToContacts, setCreateNewAddition, setAppealingForTaxpayer, setDuplicateClassificationAppeal, setDuplicateAbatementDeductionAppeal,
  setDuplicateVeteranDeductionAppeal, setDuplicateDisabledVeteranExemptionAppeal, setDuplicateSeniorCitizenOrDisabledPersonAppeal,
  restorePendingAppealState
} = appealSlice.actions;

export const selectFlow = (state: RootState) => state.newAppeal.flow;
export const selectAppealYear = (state: RootState) => state.newAppeal.appeal.appealYear;
export const selectCounty = (state: RootState) => state.newAppeal.appeal.county;
export const selectMunicipality = (state: RootState) => state.newAppeal.appeal.municipality;
export const selectIsTenAppeal = (state: RootState) => state.newAppeal.appeal.tenAppeal;
export const selectTenRecords = (state: RootState) => state.newAppeal.appeal.tenRecords;
export const selectAppeal = (state: RootState) => state.newAppeal.appeal;
export const selectCurrentAssessmentLand = (state: RootState) => state.newAppeal.appeal.property.currentAssessment.land;
export const selectCurrentAssessmentBuildingImprovement = (state: RootState) => state.newAppeal.appeal.property.currentAssessment.buildingImprovement;
export const selectCurrentAssessmentAbatement = (state: RootState) => state.newAppeal.appeal.property.currentAssessment.abatement;
export const selectCurrentAssessmentTotal = (state: RootState) => state.newAppeal.appeal.property.currentAssessment.total;
export const selectCurrentAdditionAmount = (state: RootState) => {
  const originalRecord = state.newAppeal.appeal.improvementRecord.originalRecord;
  return originalRecord
    ? (originalRecord.additionImprovement + originalRecord.additionLand - originalRecord.additionAbatement) * (Number(originalRecord.totalMonthsAssessed) / 12)
    : 0;
};
export const selectAssessmentAddition = (state: RootState) => state.newAppeal.appeal.improvementRecord.originalRecord;
export const selectAdditionListType = (state: RootState) => state.newAppeal.appeal.improvementRecord.listType;
export const selectAdditionTaxYear = (state: RootState) => state.newAppeal.appeal.improvementRecord.taxYear;
export const selectAdditionPropertyClass = (state: RootState) => state.newAppeal.appeal.improvementRecord.propertyClass;
export const selectRequestedAdditionLand = (state: RootState) => state.newAppeal.appeal.improvementRecord.requestedAdditionLand;
export const selectRequestedAdditionImprovement = (state: RootState) => state.newAppeal.appeal.improvementRecord.requestedAdditionImprovement;
export const selectRequestedAdditionAbatement = (state: RootState) => state.newAppeal.appeal.improvementRecord.requestedAdditionAbatement;
export const selectRequestedAdditionAmount = (state: RootState) => state.newAppeal.appeal.improvementRecord.requestedAdditionAmount;
export const selectRequestedAdditionDate = (state: RootState) => state.newAppeal.appeal.improvementRecord.requestedAdditionDate;
export const selectRequestedAdditionMonth = (state: RootState) => state.newAppeal.appeal.improvementRecord.requestedAdditionMonth;
export const selectRequestedPropertyClassification = (state: RootState) => state.newAppeal.appeal.classification?.requestedClass;
export const selectAppealingClassification = (state: RootState) => state.newAppeal.appeal.classification.appealing;
export const selectAppealingValuation = (state: RootState) => state.newAppeal.appeal.valuation.appealing;
export const selectRequestedTotal = (state: RootState) => state.newAppeal.appeal.valuation.requestedTotal;
export const selectReasonForAppeal = (state: RootState) => state.newAppeal.appeal.valuation.reason;
export const selectTaxCourtPending = (state: RootState) => state.newAppeal.appeal.valuation.taxCourtPending;
export const selectPurchasePrice = (state: RootState) => state.newAppeal.appeal.valuation.purchasePrice;
export const selectPurchaseDate = (state: RootState) => state.newAppeal.appeal.valuation.purchaseDate;
export const selectProperty = (state: RootState) => state.newAppeal.appeal.property;
export const selectPropertyClassification = (state: RootState) => state.newAppeal.appeal.property.classification;
export const selectAdditionalContactIsLawyer = (state: RootState) => state.newAppeal.appeal.additionalContact?.isLawyer;
export const selectContactName = (state: RootState) => state.newAppeal.appeal.contact.firstName;
export const selectContactEmailAddress = (state: RootState) => state.newAppeal.appeal.contact.emailAddress;
export const selectContactDayTimePhone = (state: RootState) => state.newAppeal.appeal.contact.dayTimePhone;
export const selectAdditionalContactName = (state: RootState) => state.newAppeal.appeal.additionalContact.firstName;
export const selectAdditionalContactEmailAddress = (state: RootState) => state.newAppeal.appeal.additionalContact.emailAddress;
export const selectAdditionalContactDayTimePhone = (state: RootState) => state.newAppeal.appeal.additionalContact.dayTimePhone;
export const selectHasAdditionalContact = (state: RootState) => state.newAppeal.appeal.additionalContact.isAdditionalContact;
export const selectHasThirdPartyContact = (state: RootState) => state.newAppeal.appeal.thirdPartyContact?.isThirdPartyContact;
export const selectHasAuthorizedAttorneyContact = (state: RootState) => !!state.newAppeal.appeal.authorizedAttorneyContact;
export const selectContact = (state: RootState) => state.newAppeal.appeal.contact;
export const selectAdditionalContact = (state: RootState) => state.newAppeal.appeal.additionalContact;
export const selectThirdPartyContact = (state: RootState) => state.newAppeal.appeal.thirdPartyContact;
export const selectAuthorizedAttorneyContact = (state: RootState) => state.newAppeal.appeal.authorizedAttorneyContact;
export const selectFilingDescription = (state: RootState) => state.newAppeal.appeal.filingDescription;

export const selectBatchAppealList = (state: RootState) => state.newAppeal.batchAppealList;
export const selectExistingAppeals = (state: RootState) => state.newAppeal.existingAppeals;
export const selectAppealsThatMayBeCrossed = (state: RootState) => state.newAppeal.appealsThatMayBeCrossed;
export const selectExistingAppealsForBLQ = (state: RootState) => state.newAppeal.matchingAppealsForBLQ;
export const selectExistingAppealsRestStatus = (state: RootState) => state.newAppeal.existingAppealsRestStatus;
export const selectExistingAppealsError = (state: RootState) => state.newAppeal.existingAppealsRestStatus?.error;
export const selectFees = (state: RootState) => state.newAppeal.appeal.fees;
export const selectFeeLoaded = (state: RootState) => state.newAppeal.feeRestStatus?.status === RestStatus.LOADED;
export const selectDeductionVeteranDeduction = (state: RootState) => state.newAppeal.appeal.deduction.veteranDeduction;
export const selectDeductionDisabledVeteranExemption = (state: RootState) => state.newAppeal.appeal.deduction.disabledVeteranExemption;
export const selectDeductionSeniorCitizenOrDisabledPerson = (state: RootState) => state.newAppeal.appeal.deduction.seniorCitizenOrDisabledPerson;
export const selectDeductionAbatement = (state: RootState) => state.newAppeal.appeal.deduction.abatement;
export const selectEntryPaymentMethod = (state: RootState) => state.newAppeal.appeal.entryPaymentMethod;
export const selectReceiptId = (state: RootState) => state.newAppeal.appeal.receiptId;
export const selectEntryMethod = (state: RootState) => state.newAppeal.appeal.entryMethod;
export const selectHearingType = (state: RootState) => state.newAppeal.appeal.hearingType;
export const selectScanDenial = (state: RootState) => state.newAppeal.appeal.scanDenial;
export const selectPreferencesCopiedToContacts = (state: RootState) => state.newAppeal.preferencesCopiedToContacts;
export const selectCreateNewAddition = (state: RootState) => state.newAppeal.createNewAddition;
export const selectAppealingForTaxpayer = (state: RootState) => state.newAppeal.appealingForTaxpayer;
export const selectAppealDuplication = (state: RootState) => state.newAppeal.appealDuplication;
export const selectDuplicateValuationAppeal = (state: RootState) => state.newAppeal.appealDuplication.valuation;
export const selectDuplicateClassificationAppeal = (state: RootState) => state.newAppeal.appealDuplication.classification;
export const selectDuplicateAdditionAppeal = (state: RootState) => state.newAppeal.appealDuplication.addition;
export const selectDuplicateAbatementDeductionAppeal = (state: RootState) => state.newAppeal.appealDuplication.abatement;
export const selectDuplicateVeteranDeductionAppeal = (state: RootState) => state.newAppeal.appealDuplication.veteranDeduction;
export const selectDuplicateDisabledVeteranExemptionAppeal = (state: RootState) => state.newAppeal.appealDuplication.disabledVeteranExemption;
export const selectDuplicateSeniorCitizenOrDisabledPersonAppeal = (state: RootState) => state.newAppeal.appealDuplication.seniorCitizenOrDisabledPerson;
export const selectCrossingValuationAppeal = (state: RootState) => state.newAppeal.crossingAppeals.valuation.isCrossAppeal;
export const selectCrossingClassificationAppeal = (state: RootState) => state.newAppeal.crossingAppeals.classification.isCrossAppeal;
export const selectCrossingAddOmitAppeal = (state: RootState) => state.newAppeal.crossingAppeals.addOmit.isCrossAppeal;
export const selectCrossingDeductionAbatementAppeal = (state: RootState) => state.newAppeal.crossingAppeals.deduction.abatement.isCrossAppeal;
export const selectCrossingDeductionVeteranAppeal = (state: RootState) => state.newAppeal.crossingAppeals.deduction.veteranDeduction.isCrossAppeal;
export const selectCrossingDeductionDisabledVeteranAppeal = (state: RootState) => state.newAppeal.crossingAppeals.deduction.disabledVeteranExemption.isCrossAppeal;
export const selectCrossingDeductionSeniorAppeal = (state: RootState) => state.newAppeal.crossingAppeals.deduction.seniorCitizenOrDisabledPerson.isCrossAppeal;
export const selectCrossingAppeals = (state: RootState) => state.newAppeal.crossingAppeals;
export const selectFilingStatus = (state: RootState) => state.newAppeal.filingStatus;
export const selectFilingStatusRestStatus = (state: RootState) => state.newAppeal.filingStatusRestStatus;
export const selectCrossFilingStatus = (state: RootState) => state.newAppeal.crossFilingStatus;
export const selectSavedAppealNumbers = (state: RootState) => state.newAppeal.savedAppealNumbers;
export const selectFailedAppeals = (state: RootState) => state.newAppeal.failedAppeals;
export const selectSaveRestStatus = (state: RootState) => state.newAppeal.saveRestStatus;
export const selectSmartPayPendingRestStatus = (state: RootState) => state.newAppeal.smartPayPendingRestStatus;
export const selectSmartPayPollRestStatus = (state: RootState) => state.newAppeal.smartPayPollRestStatus;
export const selectSmartPayPendingStatus = (state: RootState) => state.newAppeal.smartPayPendingStatus;
export default appealSlice.reducer;
